/**
 * 结构化打标配置表单（v4.1 UI重构 + v3.1 层级打标）
 * 三步配置流程：选择列(表格) → Prompt(IDE编辑器) → 模型(下拉框) + 试标/全量打标
 * 步骤由父组件 NewTaskPage 统一管理
 */
import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spin, Space, Select, message, Tag, Typography, Modal, Input, Switch, Upload, Divider } from 'antd';
import { ThunderboltOutlined, EyeOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';
import { getExcelColumnsWithTypes, submitTrialRequest, estimateCost, getAvailableModels, getTemplates, createTemplate } from '../services/api';
import ColumnGrid from './ColumnGrid';
import PromptEditor from './PromptEditor';
import TrialResultModal from './TrialResultModal';
import EstimationModal from './EstimationModal';
import './ConfigurationForm.css';

const { TextArea } = Input;

const ConfigurationForm = ({ taskId, filename, onSubmit, configStep, onStepChange }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [rowCount, setRowCount] = useState(0);
  const [error, setError] = useState(null);

  const [columnConfigs, setColumnConfigs] = useState([]);
  const [promptValue, setPromptValue] = useState('');

  // v3.1 层级打标相关状态
  const [hierarchyEnabled, setHierarchyEnabled] = useState(false);
  const [taxonomyFile, setTaxonomyFile] = useState(null);
  const [hierarchyPrompt, setHierarchyPrompt] = useState('');
  const [taxonomyUploading, setTaxonomyUploading] = useState(false);
  const [taxonomyMaxDepth, setTaxonomyMaxDepth] = useState(null); // 标签树最大深度

  // 试打相关状态
  const [trialLoading, setTrialLoading] = useState(false);
  const [trialResult, setTrialResult] = useState(null);
  const [showTrialModal, setShowTrialModal] = useState(false);

  // 成本预估相关状态
  const [estimationLoading, setEstimationLoading] = useState(false);
  const [estimation, setEstimation] = useState(null);
  const [showEstimationModal, setShowEstimationModal] = useState(false);

  // 模型选择相关状态
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [modelsLoading, setModelsLoading] = useState(false);

  // 模板相关状态
  const [userTemplates, setUserTemplates] = useState([]);
  const [saveTemplateModalVisible, setSaveTemplateModalVisible] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [saveTemplateLoading, setSaveTemplateLoading] = useState(false);
  const isLoggedIn = !!localStorage.getItem('auth_token');

  // 加载Excel列名和类型
  useEffect(() => {
    const loadColumnsWithTypes = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getExcelColumnsWithTypes(taskId);
        if (response.success) {
          const configs = (response.columns || []).map(col => ({
            columnName: col.name,
            detectedType: col.detected_type,
            includeInLabeling: false,
            userOverrideType: null,
            sampleValues: col.sample_values || [],
          }));
          setColumnConfigs(configs);
          setRowCount(response.row_count || 0);
        } else {
          setError('获取列名失败');
        }
      } catch (err) {
        setError(err.response?.data?.detail || '加载列名失败，请重试');
      } finally {
        setLoading(false);
      }
    };
    if (taskId) loadColumnsWithTypes();
  }, [taskId]);

  // 计算当前选中列的有效类型
  const effectiveDataType = React.useMemo(() => {
    const selected = columnConfigs.filter(c => c.includeInLabeling);
    if (selected.length === 0) return 'TEXT';
    if (selected.some(c => (c.userOverrideType || c.detectedType) === 'video')) return 'VIDEO';
    if (selected.some(c => ['image', 'object'].includes(c.userOverrideType || c.detectedType))) return 'IMAGE';
    return 'TEXT';
  }, [columnConfigs]);

  const deepSeekCompatible = React.useMemo(() => {
    const selected = columnConfigs.filter(c => c.includeInLabeling);
    return selected.length > 0 && selected.every(c => (c.userOverrideType || c.detectedType) === 'text');
  }, [columnConfigs]);

  // 保留原函数用于向后兼容
  const getEffectiveDataType = () => effectiveDataType;
  const canUseDeepSeek = () => deepSeekCompatible;

  // 加载可用模型列表
  useEffect(() => {
    const loadModels = async () => {
      try {
        setModelsLoading(true);
        const response = await getAvailableModels(effectiveDataType);
        if (response.success) {
          let models = response.models || [];
          if (!deepSeekCompatible) {
            models = models.filter(m => !m.model_id.toLowerCase().includes('deepseek'));
          }
          setAvailableModels(models);
          const defaultModel = response.default_model;
          if (defaultModel && models.some(m => m.model_id === defaultModel)) {
            setSelectedModel(defaultModel);
          } else if (models.length > 0) {
            setSelectedModel(models[0].model_id);
          }
        }
      } catch (err) {
        setAvailableModels([]);
        setSelectedModel('gemini-2.5-flash');
      } finally {
        setModelsLoading(false);
      }
    };
    if (configStep >= 1) loadModels();
  }, [configStep, effectiveDataType, deepSeekCompatible]);

  // 加载用户模板列表
  useEffect(() => {
    const loadUserTemplates = async () => {
      if (!isLoggedIn) return;
      try {
        const res = await getTemplates();
        if (res.success) setUserTemplates(res.templates || []);
      } catch { /* 静默失败 */ }
    };
    if (configStep === 1) loadUserTemplates();
  }, [configStep, isLoggedIn]);

  // 保存当前指令为模板
  const handleSaveAsTemplate = async () => {
    if (!saveTemplateName.trim()) { message.warning('请输入模板名称'); return; }
    try {
      setSaveTemplateLoading(true);
      // v3.1: 根据层级打标状态选择保存的内容
      const contentToSave = hierarchyEnabled ? hierarchyPrompt : promptValue;
      const res = await createTemplate({ name: saveTemplateName, content: contentToSave });
      if (res.success) {
        message.success('模板保存成功');
        setSaveTemplateModalVisible(false);
        setSaveTemplateName('');
        // 刷新模板列表
        const listRes = await getTemplates();
        if (listRes.success) setUserTemplates(listRes.templates || []);
      }
    } catch {
      message.error('保存模板失败');
    } finally {
      setSaveTemplateLoading(false);
    }
  };

  const handleModelChange = (modelId) => {
    setSelectedModel(modelId);
    setTrialResult(null);
    setEstimation(null);
  };

  const handleTypeChange = (columnName, newType) => {
    setColumnConfigs(prev => prev.map(c =>
      c.columnName === columnName ? { ...c, userOverrideType: newType } : c
    ));
    setTrialResult(null);
    setEstimation(null);
  };

  const handleIncludeChange = (columnName, included) => {
    setColumnConfigs(prev => prev.map(c =>
      c.columnName === columnName ? { ...c, includeInLabeling: included } : c
    ));
    setTrialResult(null);
    setEstimation(null);
  };

  const buildFlexibleConfig = () => ({
    columns: columnConfigs.filter(c => c.includeInLabeling).map(c => ({
      column_name: c.columnName,
      column_type: c.userOverrideType || c.detectedType,
    }))
  });

  // 成本预估
  const handleEstimate = async () => {
    try {
      setEstimationLoading(true);
      const selected = columnConfigs.filter(c => c.includeInLabeling);
      if (selected.length === 0) throw new Error('请至少选择一个列进行打标');

      // v3.1: 层级打标时检查 hierarchyPrompt，否则检查 promptValue
      if (hierarchyEnabled) {
        if (!hierarchyPrompt) throw new Error('请输入层级打标的基础 Prompt');
        if (!taxonomyFile) throw new Error('请先上传标签树文件');

        // 如果还没有上传标签树获取深度，先上传
        if (!taxonomyMaxDepth) {
          message.info('正在上传标签树文件...');
          const formData = new FormData();
          formData.append('task_id', taskId);
          formData.append('taxonomy_file', taxonomyFile);

          const uploadResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/upload-taxonomy`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
            },
            body: formData
          });

          if (!uploadResponse.ok) {
            throw new Error('标签树上传失败，无法预估成本');
          }

          const uploadResult = await uploadResponse.json();
          if (uploadResult.max_depth) {
            setTaxonomyMaxDepth(uploadResult.max_depth);
            message.success(`标签树上传成功（${uploadResult.max_depth}层）`);
          }
        }
      } else {
        if (!promptValue) throw new Error('请输入打标指令');
      }

      const config = {
        taskId, targetColumn: '__flexible__', dataType: 'FLEXIBLE',
        promptInstructions: hierarchyEnabled ? hierarchyPrompt : promptValue,
        model_id: selectedModel,
        flexible_config: buildFlexibleConfig(),
      };
      const response = await estimateCost(config);
      if (response.success) {
        // v3.1: 如果启用层级打标，需要乘以层级深度
        let adjustedEstimation = { ...response };

        if (hierarchyEnabled && taxonomyMaxDepth) {
          // 层级打标会进行多层调用，成本需要乘以深度
          adjustedEstimation.estimated_total_cost = response.estimated_total_cost * taxonomyMaxDepth;
          adjustedEstimation.estimated_tokens = response.estimated_tokens * taxonomyMaxDepth;
          adjustedEstimation.avg_cost_per_row = response.avg_cost_per_row * taxonomyMaxDepth;
          adjustedEstimation.hierarchy_multiplier = taxonomyMaxDepth; // 标记已调整
        }

        setEstimation(adjustedEstimation);
        setShowEstimationModal(true);
      } else {
        message.error(response.error || '成本预估失败，请重试');
      }
    } catch (err) {
      message.error(err.response?.data?.detail || err.message || '成本预估失败，请重试');
    } finally {
      setEstimationLoading(false);
    }
  };

  // 确认提交
  const handleConfirmAndSubmit = async () => {
    try {
      setShowEstimationModal(false);

      // v3.1: 如果启用层级打标，先上传标签树
      if (hierarchyEnabled) {
        if (!taxonomyFile) {
          message.error('请上传标签树文件');
          return;
        }
        if (!hierarchyPrompt) {
          message.error('请输入层级打标的基础 Prompt');
          return;
        }

        // 上传标签树文件
        setTaxonomyUploading(true);
        try {
          const formData = new FormData();
          formData.append('task_id', taskId);
          formData.append('taxonomy_file', taxonomyFile);

          const uploadResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/upload-taxonomy`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
            },
            body: formData
          });

          if (!uploadResponse.ok) {
            throw new Error('标签树上传失败');
          }

          const uploadResult = await uploadResponse.json();
          message.success('标签树上传成功');
          console.log('Taxonomy upload result:', uploadResult);

          // 保存层级深度用于成本计算
          if (uploadResult.max_depth) {
            setTaxonomyMaxDepth(uploadResult.max_depth);
          }
        } catch (err) {
          message.error(err.message || '标签树上传失败');
          setTaxonomyUploading(false);
          return;
        } finally {
          setTaxonomyUploading(false);
        }
      }

      // v3.1: 根据模式构建不同的配置
      // v3.2: 层级模式也传递 flexible_config
      const config = hierarchyEnabled ? {
        // 层级打标模式：使用 FLEXIBLE 引擎
        taskId,
        targetColumn: '__flexible__',  // 使用 FLEXIBLE 模式
        dataType: 'FLEXIBLE',
        promptInstructions: hierarchyPrompt,
        model_id: selectedModel,
        flexible_config: buildFlexibleConfig(),  // v3.2: 传递列配置
        estimated_cost: estimation?.estimated_total_cost,
        estimated_tokens: estimation?.estimated_tokens,
        hierarchy_enabled: true,
        hierarchy_prompt: hierarchyPrompt,
      } : {
        // FLEXIBLE 模式：传递 FLEXIBLE 相关参数
        taskId,
        targetColumn: '__flexible__',
        dataType: 'FLEXIBLE',
        promptInstructions: promptValue,
        model_id: selectedModel,
        flexible_config: buildFlexibleConfig(),
        estimated_cost: estimation?.estimated_total_cost,
        estimated_tokens: estimation?.estimated_tokens,
        hierarchy_enabled: false,
        hierarchy_prompt: null,
      };

      // 🔍 调试日志：提交前检查状态
      console.log('=== 提交配置前的状态检查 ===');
      console.log('hierarchyEnabled:', hierarchyEnabled);
      console.log('hierarchyPrompt:', hierarchyPrompt);
      console.log('taxonomyFile:', taxonomyFile);
      console.log('config.hierarchy_enabled:', config.hierarchy_enabled);
      console.log('config.hierarchy_prompt:', config.hierarchy_prompt);
      console.log('完整 config:', config);
      console.log('================================');

      if (onSubmit) await onSubmit(config);
    } catch (err) {
      // 处理验证错误（FastAPI 返回的 detail 可能是数组）
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        // FastAPI 验证错误格式：[{type, loc, msg, input}]
        const errorMsg = detail.map(e => e.msg).join('; ');
        message.error(errorMsg || '提交失败，请重试');
      } else {
        message.error(detail || '提交失败，请重试');
      }
    }
  };

  // 试打
  const handleTrial = async () => {
    try {
      setTrialLoading(true);
      const selected = columnConfigs.filter(c => c.includeInLabeling);
      if (selected.length === 0) throw new Error('请至少选择一个列进行打标');

      // v3.1: 层级打标时检查 hierarchyPrompt，否则检查 promptValue
      if (hierarchyEnabled) {
        if (!hierarchyPrompt) throw new Error('请输入层级打标的基础 Prompt');
        if (!taxonomyFile) throw new Error('请先上传标签树文件');

        // 如果还没有上传标签树获取深度，先上传
        if (!taxonomyMaxDepth) {
          message.info('正在上传标签树文件...');
          const formData = new FormData();
          formData.append('task_id', taskId);
          formData.append('taxonomy_file', taxonomyFile);

          const uploadResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/upload-taxonomy`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
            },
            body: formData
          });

          if (!uploadResponse.ok) {
            throw new Error('标签树上传失败，无法执行试打');
          }

          const uploadResult = await uploadResponse.json();
          if (uploadResult.max_depth) {
            setTaxonomyMaxDepth(uploadResult.max_depth);
            message.success(`标签树上传成功（${uploadResult.max_depth}层）`);
          }
        }
      } else {
        if (!promptValue) throw new Error('请输入打标指令');
      }

      const config = {
        taskId, targetColumn: '__flexible__', dataType: 'FLEXIBLE',
        promptInstructions: hierarchyEnabled ? hierarchyPrompt : promptValue,
        maxRows: 20,
        model_id: selectedModel, flexible_config: buildFlexibleConfig(),
        // v3.1: 层级打标配置
        hierarchy_enabled: hierarchyEnabled,
        hierarchy_prompt: hierarchyEnabled ? hierarchyPrompt : null,
      };
      const response = await submitTrialRequest(config);
      if (response.success) {
        setTrialResult(response);
        setShowTrialModal(true);
        message.success('试打完成！');
      } else {
        message.error(response.error || '试打失败，请重试');
      }
    } catch (err) {
      // 处理验证错误（FastAPI 返回的 detail 可能是数组）
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const errorMsg = detail.map(e => e.msg).join('; ');
        message.error(errorMsg || err.message || '试打失败，请重试');
      } else {
        message.error(detail || err.message || '试打失败，请重试');
      }
    } finally {
      setTrialLoading(false);
    }
  };

  // 步骤切换（通过父组件回调）
  const handleStepChange = (step) => {
    if (step > 0 && configStep === 0) {
      if (columnConfigs.filter(c => c.includeInLabeling).length === 0) {
        message.warning('请至少选择一个列进行打标');
        return;
      }
    }
    onStepChange(step);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'image': return 'blue';
      case 'video': return 'purple';
      case 'object': return 'cyan';
      default: return 'default';
    }
  };

  // 获取选中列的列名列表（供 PromptEditor 自动补全）
  const selectedColumnNames = columnConfigs
    .filter(c => c.includeInLabeling)
    .map(c => c.columnName);

  const selectedCount = selectedColumnNames.length;

  if (loading) {
    return (
      <Card title="配置打标任务">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px' }}>正在加载Excel列信息...</p>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card title="配置打标任务">
        <Alert message="加载失败" description={error} type="error" showIcon />
      </Card>
    );
  }

  return (
    <Card title="配置打标任务" className="configuration-form">
      <Form form={form} layout="vertical">
        {/* 步骤1: 选择列和类型 - 表格 */}
        {configStep === 0 && (
          <div className="step-content">
            <Alert
              message={`文件: ${filename} | 共 ${rowCount} 行数据`}
              description="选择要加入打标的列，并确认或修改列类型。系统会根据列名自动识别类型。"
              type="info"
              showIcon
              style={{ marginBottom: '24px' }}
            />

            <ColumnGrid
              columnConfigs={columnConfigs}
              onIncludeChange={handleIncludeChange}
              onTypeChange={handleTypeChange}
            />

            {selectedCount > 0 && (
              <Alert
                message={`已选择 ${selectedCount} 列`}
                description={
                  <Space wrap>
                    {columnConfigs.filter(c => c.includeInLabeling).map(c => (
                      <Tag key={c.columnName} color={getTypeColor(c.userOverrideType || c.detectedType)}>
                        {c.columnName} ({c.userOverrideType || c.detectedType})
                      </Tag>
                    ))}
                  </Space>
                }
                type="success"
                showIcon
                style={{ marginTop: '16px', marginBottom: '16px' }}
              />
            )}

            {selectedCount === 0 && (
              <Alert
                message="请至少选择一个列进行打标"
                type="warning"
                showIcon
                style={{ marginTop: '16px', marginBottom: '16px' }}
              />
            )}

            <Button
              type="primary"
              size="large"
              block
              onClick={() => handleStepChange(1)}
              disabled={selectedCount === 0}
            >
              下一步：输入打标指令
            </Button>
          </div>
        )}

        {/* 步骤2: Prompt IDE 编辑器 */}
        {configStep === 1 && (
          <div className="step-content">
            {/* 显示已选择的列 */}
            <div style={{ marginBottom: '16px' }}>
              <span style={{ fontWeight: 'bold' }}>已选择的列：</span>
              <Space wrap style={{ marginLeft: '8px' }}>
                {columnConfigs.filter(c => c.includeInLabeling).map(c => (
                  <Tag key={c.columnName} color={getTypeColor(c.userOverrideType || c.detectedType)}>
                    {c.columnName} ({c.userOverrideType || c.detectedType})
                  </Tag>
                ))}
              </Space>
            </div>

            {/* 普通打标指令（层级打标关闭时显示） */}
            {!hierarchyEnabled && (
              <>
                <PromptEditor
                  value={promptValue}
                  onChange={setPromptValue}
                  availableColumns={selectedColumnNames}
                />

                {/* 模板区域 */}
                {isLoggedIn && (
                  <div style={{ marginTop: '16px' }}>
                    <span style={{ fontWeight: 'bold', marginRight: '8px' }}>我的模板：</span>
                    {userTemplates.length === 0 ? (
                      <span style={{ color: 'var(--color-text-tertiary)', fontSize: '13px' }}>暂无模板，可在编写指令后保存</span>
                    ) : (
                      <Space wrap>
                        {userTemplates.map(t => (
                          <Tag
                            key={t.template_id}
                            color="blue"
                            style={{ cursor: 'pointer' }}
                            onClick={() => { setPromptValue(t.template_content); message.success(`已加载模板「${t.template_name}」`); }}
                          >
                            {t.template_name}
                          </Tag>
                        ))}
                      </Space>
                    )}
                  </div>
                )}
              </>
            )}

            {/* v3.1 层级打标配置区域 - 移到底部 */}
            {hierarchyEnabled && (
              <Card style={{ marginTop: 16, background: 'var(--color-bg-hover)' }} size="small">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Alert
                    message="层级打标说明"
                    description="上传标签树 Excel 文件，系统将按层级逐步打标，每层只从少量候选中选择，提高准确性。"
                    type="info"
                    showIcon
                  />

                  <Divider style={{ margin: '12px 0' }}>上传标签树</Divider>
                  <Upload
                    beforeUpload={(file) => {
                      setTaxonomyFile(file);
                      return false;
                    }}
                    accept=".xlsx,.xls"
                    maxCount={1}
                    onRemove={() => setTaxonomyFile(null)}
                  >
                    <Button icon={<UploadOutlined />}>选择标签树 Excel</Button>
                  </Upload>

                  {taxonomyFile && (
                    <Alert
                      message="标签树已选择"
                      description={`文件名: ${taxonomyFile.name}`}
                      type="success"
                      showIcon
                    />
                  )}

                  <Divider style={{ margin: '12px 0' }}>基础 Prompt</Divider>
                  <PromptEditor
                    value={hierarchyPrompt}
                    onChange={setHierarchyPrompt}
                    availableColumns={selectedColumnNames}
                  />

                  {/* 层级打标的模板区域 */}
                  {isLoggedIn && (
                    <div style={{ marginTop: '16px' }}>
                      <span style={{ fontWeight: 'bold', marginRight: '8px' }}>我的模板：</span>
                      {userTemplates.length === 0 ? (
                        <span style={{ color: 'var(--color-text-tertiary)', fontSize: '13px' }}>暂无模板，可在编写指令后保存</span>
                      ) : (
                        <Space wrap>
                          {userTemplates.map(t => (
                            <Tag
                              key={t.template_id}
                              color="blue"
                              style={{ cursor: 'pointer' }}
                              onClick={() => { setHierarchyPrompt(t.template_content); message.success(`已加载模板「${t.template_name}」`); }}
                            >
                              {t.template_name}
                            </Tag>
                          ))}
                        </Space>
                      )}
                    </div>
                  )}
                </Space>
              </Card>
            )}

            {/* 层级打标开关 - 移到底部 */}
            <Card style={{ marginTop: 16, background: 'var(--color-bg-hover)' }} size="small">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 'bold' }}>层级打标（实验性功能）</span>
                <Switch
                  checked={hierarchyEnabled}
                  onChange={setHierarchyEnabled}
                  checkedChildren="已开启"
                  unCheckedChildren="关闭"
                />
              </div>
            </Card>

            <Space style={{ width: '100%', marginTop: '24px' }} size="middle">
              <Button size="large" onClick={() => handleStepChange(0)}>
                上一步
              </Button>
              {isLoggedIn && (hierarchyEnabled ? hierarchyPrompt : promptValue) && (
                <Button
                  size="large"
                  icon={<SaveOutlined />}
                  onClick={() => setSaveTemplateModalVisible(true)}
                >
                  保存为模板
                </Button>
              )}
              <Button
                type="primary"
                size="large"
                style={{ flex: 1 }}
                onClick={() => handleStepChange(2)}
              >
                下一步：选择模型
              </Button>
            </Space>

            {/* 保存为模板弹窗 */}
            <Modal
              title="保存为模板"
              open={saveTemplateModalVisible}
              onOk={handleSaveAsTemplate}
              onCancel={() => setSaveTemplateModalVisible(false)}
              confirmLoading={saveTemplateLoading}
              okText="保存"
              cancelText="取消"
            >
              <div style={{ marginBottom: 8 }}>
                <label style={{ fontWeight: 'bold' }}>模板名称</label>
              </div>
              <Input
                placeholder="例如：情感分析、分类打标"
                value={saveTemplateName}
                onChange={e => setSaveTemplateName(e.target.value)}
                maxLength={100}
              />
            </Modal>
          </div>
        )}

        {/* 步骤3: 模型选择下拉框 + 试标/执行 */}
        {configStep === 2 && (
          <div className="step-content">
            {!canUseDeepSeek() && (
              <Typography.Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                当前选择的列包含图片/视频/主体类型，DeepSeek 仅支持纯文本打标
              </Typography.Text>
            )}

            <div style={{ marginBottom: '16px' }}>
              <span style={{ fontWeight: 'bold', marginRight: '12px' }}>选择模型：</span>
              {modelsLoading ? (
                <Spin size="small" />
              ) : (
                <Select
                  value={selectedModel}
                  onChange={handleModelChange}
                  style={{ width: '100%', maxWidth: 300 }}
                  options={availableModels.map(m => ({
                    value: m.model_id,
                    label: m.display_name + (m.is_default ? ' (推荐)' : ''),
                  }))}
                />
              )}
            </div>

            {/* 配置摘要 */}
            <div style={{
              background: 'var(--color-bg-hover)',
              padding: '16px',
              borderRadius: 'var(--radius-card)',
              marginTop: '16px',
              marginBottom: '24px',
            }}>
              <h4 style={{ marginBottom: '12px' }}>配置摘要</h4>
              <p><strong>文件：</strong>{filename}</p>
              <p><strong>行数：</strong>{rowCount} 行</p>
              <p><strong>选中列：</strong>
                <Space wrap style={{ marginLeft: '8px' }}>
                  {columnConfigs.filter(c => c.includeInLabeling).map(c => (
                    <Tag key={c.columnName} color={getTypeColor(c.userOverrideType || c.detectedType)}>
                      {c.columnName}
                    </Tag>
                  ))}
                </Space>
              </p>
              <p><strong>模型：</strong>{selectedModel}</p>
              <p><strong>指令：</strong>{promptValue?.substring(0, 100)}{promptValue?.length > 100 ? '...' : ''}</p>
            </div>

            {/* 操作按钮 */}
            <Space style={{ width: '100%' }} size="middle">
              <Button size="large" onClick={() => handleStepChange(1)}>
                上一步
              </Button>
              <Button
                size="large"
                icon={<EyeOutlined />}
                onClick={handleTrial}
                loading={trialLoading}
              >
                试标（前20行）
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<ThunderboltOutlined />}
                style={{ flex: 1 }}
                onClick={handleEstimate}
                loading={estimationLoading}
              >
                预估成本并执行
              </Button>
            </Space>
          </div>
        )}
      </Form>

      {/* 试打结果弹窗 */}
      <TrialResultModal
        visible={showTrialModal}
        onClose={() => setShowTrialModal(false)}
        data={trialResult}
      />

      {/* 成本预估弹窗 */}
      <EstimationModal
        visible={showEstimationModal}
        onCancel={() => setShowEstimationModal(false)}
        estimation={estimation}
        onConfirm={handleConfirmAndSubmit}
        hierarchyEnabled={hierarchyEnabled}
      />
    </Card>
  );
};

export default ConfigurationForm;